import { useEffect, useState } from 'react'
import { env } from '@/env'

/**
 * Browser types for extension installation
 */
export type BrowserType = 'chrome' | 'edge' | 'firefox' | 'safari' | 'unknown'

/**
 * Get the user's browser type
 */
export function getBrowserType(): BrowserType {
  if (typeof window === 'undefined') return 'unknown'

  const userAgent = navigator.userAgent.toLowerCase()

  // Edge must be checked before Chrome since Edge includes "chrome" in UA
  if (userAgent.includes('edg/')) return 'edge'
  if (userAgent.includes('chrome')) return 'chrome'
  if (userAgent.includes('firefox')) return 'firefox'
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari'

  return 'unknown'
}

/**
 * Get the extension store URL for the current browser
 */
export function getExtensionStoreUrl(browser?: BrowserType): string {
  const browserType = browser || getBrowserType()

  switch (browserType) {
    case 'chrome':
      return env.VITE_CHROME_EXTENSION_URL
    case 'edge':
      return env.VITE_EDGE_EXTENSION_URL
    case 'firefox':
      return env.VITE_FIREFOX_EXTENSION_URL
    case 'safari':
      return env.VITE_SAFARI_EXTENSION_URL
    default:
      // Default to Chrome store
      return env.VITE_CHROME_EXTENSION_URL
  }
}

/**
 * Hook to detect if the Fiddl extension is installed
 * Checks for the meta tag injected by the extension
 */
export function useExtensionDetection() {
  const [isInstalled, setIsInstalled] = useState<boolean>(false)
  const [isChecking, setIsChecking] = useState<boolean>(true)

  useEffect(() => {
    let isMounted = true

    // Check immediately
    const checkExtension = () => {
      if (!isMounted) return
      const meta = document.querySelector('meta[name="fiddl_ext"]')
      setIsInstalled(meta?.getAttribute('content') === 'true')
      setIsChecking(false)
    }

    checkExtension()

    // Also check after a short delay in case extension loads after page
    const timeout = setTimeout(checkExtension, 1000)

    // Set up a MutationObserver to detect when the meta tag is added
    const observer = new MutationObserver((mutations) => {
      if (!isMounted) return

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const meta = document.querySelector('meta[name="fiddl_ext"]')
          if (meta?.getAttribute('content') === 'true') {
            setIsInstalled(true)
            setIsChecking(false)
            observer.disconnect()
            break
          }
        }
      }
    })

    observer.observe(document.head, {
      childList: true,
      subtree: true,
    })

    return () => {
      isMounted = false
      clearTimeout(timeout)
      observer.disconnect()
    }
  }, [])

  return {
    isInstalled,
    isChecking,
    browserType: getBrowserType(),
    storeUrl: getExtensionStoreUrl(),
  }
}
